import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DeleteCreatorSetResponse,
  GetCreatorInsightsRequestBody,
  GetCreatorInsightsResponse,
  GetCreatorSetsResponse,
  SaveCreatorSetBody,
  SaveCreatorSetInput,
  SaveCreatorSetResponse
} from '@blisspointmedia/bpm-types/dist/YoutubeCreatorSets';
import { YouTubeChannelInsights } from '@blisspointmedia/bpm-types/dist/YoutubeCreatorInsights';
import { YouTubeCreatorSetMember } from '@blisspointmedia/bpm-types/dist/pgTables/YoutubeCreatorSetMember';
import { awaitJSON, YoutubePlanningLambdaFetch } from '@src/utils/fetch-utils';
import { buildCreatorInsightsCriteria } from '../../Discover/creator-discovery-utils';
import { GoogleAdsAccount } from '../../youtube-planning-types';
import { CHANNEL_LOOKUP_BATCH_SIZE, MemberMetricsChange } from '../creator-sets-constants';
import { buildMetricsChanges, chunk, getChannelsById } from '../creator-sets-utils';

const creatorSetsKey = (cid: string) => ['youtubeCreatorSets', cid];

// The company's live sets, newest edit first, members in curated order.
export const useCreatorSets = (cid: string): ReturnType<typeof useQuery<GetCreatorSetsResponse>> =>
  useQuery({
    queryKey: creatorSetsKey(cid),
    queryFn: async (): Promise<GetCreatorSetsResponse> => {
      const res = await YoutubePlanningLambdaFetch('/getCreatorSets', {
        method: 'GET',
        params: { company: cid }
      });
      return awaitJSON<GetCreatorSetsResponse>(res);
    },
    enabled: Boolean(cid)
  });

// Creates (no id) or updates a set. The response is the saved set, so callers can use it directly.
export const useSaveCreatorSet = (cid: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (creatorSet: SaveCreatorSetInput): Promise<SaveCreatorSetResponse> => {
      const body: SaveCreatorSetBody = { creatorSet };
      const res = await YoutubePlanningLambdaFetch('/saveCreatorSet', { method: 'POST', body });
      return awaitJSON<SaveCreatorSetResponse>(res);
    },
    // The saved set goes straight into the cache so an open set shows it immediately, then the list
    // refetches for the server's ordering.
    onSuccess: saved => {
      queryClient.setQueryData<GetCreatorSetsResponse>(creatorSetsKey(cid), prev =>
        prev ? [saved, ...prev.filter(set => set.id !== saved.id)] : prev
      );
      return queryClient.invalidateQueries({ queryKey: creatorSetsKey(cid) });
    }
  });
};

// Soft delete; the set leaves the list on the invalidation that follows.
export const useDeleteCreatorSet = (cid: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<DeleteCreatorSetResponse> => {
      const res = await YoutubePlanningLambdaFetch('/deleteCreatorSet', {
        method: 'DELETE',
        params: { id: String(id), company: cid }
      });
      return awaitJSON<DeleteCreatorSetResponse>(res);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: creatorSetsKey(cid) })
  });
};

// Looks channels up (full profiles, including audience and top videos) in batches, one at a time
// because the service allows 5 requests per second. `onProgress` gets the running count of channels
// asked for after each batch. A channel Google doesn't return is absent from the map.
export const lookUpChannels = async (
  cid: string,
  googleAdsAccount: GoogleAdsAccount,
  channelIds: string[],
  onProgress?: (lookedUp: number) => void
): Promise<Map<string, YouTubeChannelInsights>> => {
  const responses: GetCreatorInsightsResponse[] = [];
  let lookedUp = 0;
  for (const batch of chunk(channelIds, CHANNEL_LOOKUP_BATCH_SIZE)) {
    const body: GetCreatorInsightsRequestBody = {
      company: cid,
      googleAdsAccount: googleAdsAccount.account_id,
      mccId: googleAdsAccount.mcc_id,
      ...buildCreatorInsightsCriteria({ channelIds: batch }, googleAdsAccount.account_id)
    };
    const res = await YoutubePlanningLambdaFetch('/getCreatorInsights', { method: 'POST', body });
    responses.push(await awaitJSON<GetCreatorInsightsResponse>(res));
    lookedUp += batch.length;
    onProgress?.(lookedUp);
  }
  return getChannelsById(responses);
};

// Re-queries Google for the members' channels and returns old-vs-new snapshots. Nothing is saved:
// the caller shows the changes and saves only if the user accepts them.
export const useRefreshCreatorMetrics = (
  cid: string,
  googleAdsAccount: GoogleAdsAccount | undefined
) =>
  useMutation({
    mutationFn: async (members: YouTubeCreatorSetMember[]): Promise<MemberMetricsChange[]> => {
      if (!googleAdsAccount) {
        throw new Error('This company has no Google Ads account to refresh metrics with.');
      }
      const channelsById = await lookUpChannels(
        cid,
        googleAdsAccount,
        members.map(member => member.channel_id)
      );
      return buildMetricsChanges(members, channelsById, new Date().toISOString());
    }
  });
